import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { LanguageSwitcher } from '@/components/ui/language-switcher'
import {
  ArrowRight,
  CheckCircle2,
  Sparkles,
  Play
} from 'lucide-react'

interface PersonaCard {
  id: string
  emoji: string
  title: string
  subtitle: string
  bullets: string[]
  gradient: string
  buttonGradient: string
  popular?: boolean
  route: string
}

export function OnboardingSelectionPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()

  // Schools are who sign up first; families and pupils join through them
  const personas: PersonaCard[] = [
    {
      id: 'school-admin',
      emoji: '🏫',
      title: t('onboarding.personas.admin.title', "I'm a School Administrator"),
      subtitle: t('onboarding.personas.admin.subtitle', 'Run your school platform'),
      bullets: [
        t('onboarding.personas.admin.bullet1', 'Manage your school & staff'),
        t('onboarding.personas.admin.bullet2', 'Invite teachers & students'),
        t('onboarding.personas.admin.bullet3', 'Analytics & reporting'),
      ],
      gradient: 'from-secondary-500 to-blue-600',
      buttonGradient: 'from-secondary-500 to-blue-600 hover:from-secondary-600 hover:to-blue-700',
      popular: true,
      route: '/onboard/school-admin',
    },
    {
      id: 'teacher',
      emoji: '📚',
      title: t('onboarding.personas.teacher.title', "I'm a Teacher"),
      subtitle: t('onboarding.personas.teacher.subtitle', 'Create & manage classes'),
      bullets: [
        t('onboarding.personas.teacher.bullet1', 'Create content & lesson plans'),
        t('onboarding.personas.teacher.bullet2', 'Manage your classes'),
        t('onboarding.personas.teacher.bullet3', 'Track student progress'),
      ],
      gradient: 'from-green-500 to-emerald-500',
      buttonGradient: 'from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600',
      route: '/onboard/individual?role=teacher',
    },
    {
      id: 'parent',
      emoji: '👨‍👩‍👧',
      title: t('onboarding.personas.parent.title', "I'm a Parent"),
      subtitle: t('onboarding.personas.parent.subtitle', "Support your child's learning"),
      bullets: [
        t('onboarding.personas.parent.bullet1', "Monitor your child's progress"),
        t('onboarding.personas.parent.bullet2', 'Communicate with teachers'),
        t('onboarding.personas.parent.bullet3', 'Get learning insights'),
      ],
      gradient: 'from-secondary-500 to-secondary-500',
      buttonGradient: 'from-secondary-500 to-secondary-500 hover:from-secondary-600 hover:to-secondary-600',
      route: '/onboard/individual?role=parent',
    },
    {
      id: 'student',
      emoji: '🎓',
      title: t('onboarding.paths.quickStudent.title', "I'm a Student"),
      subtitle: t('onboarding.paths.quickStudent.subtitle', 'Learn, practice & grow'),
      bullets: [
        t('onboarding.personas.student.bullet1', 'AI-powered personalized lessons'),
        t('onboarding.personas.student.bullet2', 'Practice exercises & quizzes'),
        t('onboarding.personas.student.bullet3', 'Track your progress'),
      ],
      gradient: 'from-blue-500 to-primary-500',
      buttonGradient: 'from-blue-500 to-primary-500 hover:from-blue-600 hover:to-primary-600',
      route: '/onboard/quick-student',
    },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/50 to-secondary-50 dark:from-gray-950 dark:via-slate-900 dark:to-secondary-900">
      {/* Navigation */}
      <nav className="sticky top-0 w-full bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl z-50 border-b border-gray-200/40 dark:border-gray-700/40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
              <div className="p-2 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl shadow-md">
                <img src="/logo-white.svg" alt="AppAcademia" className="w-5 h-5" />
              </div>
              <span className="text-xl font-bold font-poppins bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
                AppAcademia
              </span>
            </div>
            <div className="flex items-center gap-2">
              <LanguageSwitcher variant="ghost" />
              <Button variant="ghost" onClick={() => navigate('/login')} className="text-sm font-medium">
                {t('onboarding.nav.signIn', 'Sign In')}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <div className="pt-16 pb-8 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold font-poppins text-gray-900 dark:text-white tracking-tight">
            {t('onboarding.howWillYouUse', 'How will you use')}{' '}
            <span className="bg-gradient-to-r from-blue-600 to-secondary-600 bg-clip-text text-transparent">
              AppAcademia
            </span>
            ?
          </h1>
          <p className="mt-4 text-lg text-gray-500 dark:text-gray-400 max-w-xl mx-auto">
            {t('onboarding.chooseRole', 'Choose your role to get a personalized experience')}
          </p>
        </div>
      </div>

      {/* Persona Cards */}
      <div className="px-4 pb-12">
        <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {personas.map((persona) => (
            <div
              key={persona.id}
              onClick={() => navigate(persona.route)}
              className="group relative bg-white dark:bg-gray-900 rounded-2xl shadow-md hover:shadow-xl border border-gray-100 dark:border-gray-800 cursor-pointer transition-all duration-300 hover:scale-[1.03] hover:-translate-y-1 flex flex-col"
            >
              {persona.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                  <Badge className="bg-gradient-to-r from-blue-500 to-primary-500 text-white border-0 shadow-md text-xs px-3">
                    <Sparkles className="w-3 h-3 mr-1" />
                    {t('onboarding.forSchools', 'For schools')}
                  </Badge>
                </div>
              )}

              <div className="p-6 flex flex-col flex-grow">
                {/* Icon */}
                <div className="text-4xl mb-4">{persona.emoji}</div>

                {/* Title */}
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                  {persona.title}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
                  {persona.subtitle}
                </p>

                {/* Bullets */}
                <div className="space-y-2.5 mb-6 flex-grow">
                  {persona.bullets.map((bullet, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-600 dark:text-gray-300">{bullet}</span>
                    </div>
                  ))}
                </div>

                {/* CTA */}
                <button
                  className={`w-full h-11 rounded-xl font-semibold text-sm text-white bg-gradient-to-r ${persona.buttonGradient} shadow-sm transition-all duration-200 flex items-center justify-center gap-2`}
                >
                  {t('onboarding.getStarted', 'Get Started')}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer links */}
      <div className="px-4 pb-20">
        <div className="max-w-md mx-auto space-y-5">
          <div className="flex flex-col items-center gap-3 pt-2">
            <button
              onClick={() => navigate('/login')}
              className="text-sm text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            >
              {t('onboarding.alreadyHaveAccount', 'Already have an account?')}{' '}
              <span className="font-semibold underline underline-offset-2">{t('onboarding.signIn', 'Sign in')}</span>
            </button>
            <button
              onClick={() => navigate('/demo')}
              className="text-sm text-gray-400 dark:text-gray-500 hover:text-secondary-600 dark:hover:text-secondary-400 transition-colors flex items-center gap-1.5"
            >
              <Play className="w-3.5 h-3.5" />
              {t('onboarding.tryDemo', 'Try Demo — No account needed')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
